/**
 * Y GO Shuttle Services France - Page Transferts REFONTE
 * Objectif : Démontrer capacité opérationnelle locale + nationale
 * Ton : Factuel, structuré, rassurant, professionnel
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plane, Train, MapPin, Clock, Phone, Calendar, Check, 
  Users, Shield, Star, Building2, Map, Network, ChevronRight,
  Loader2, Mail, CheckCircle2, Globe, Anchor
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Header, Footer } from '../components/layout';
import { useCMSPage } from '../hooks/useCMSPage';
import SafeImage from '../components/SafeImage';

const DEFAULT_HERO_IMAGE = 'https://images.unsplash.com/photo-1464037866556-6812c9d1c72e?auto=format&fit=crop&w=1600&q=80';

export default function TransfertsPage() {
  const { content: cmsContent, loading: cmsLoading } = useCMSPage('transferts');

  // Get hero data from CMS
  const banner = cmsContent?.banner || {};
  const heroImage = banner.image_url || DEFAULT_HERO_IMAGE;
  const heroTitle = banner.title || 'Transferts privés VTC';
  const heroSubtitle = banner.subtitle || 'Le Havre & toute la France';

  // SEO
  useEffect(() => {
    const meta = cmsContent?.meta || {};
    document.title = meta.title || 'Transferts VTC Le Havre, Aéroports & Gares | Y GO Shuttle';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = meta.description || 'Service de transferts VTC premium depuis Le Havre vers aéroports (CDG, Orly, Beauvais), gares et toute la France. Chauffeurs professionnels, suivi temps réel.';
    
    return () => {
      document.title = 'Y GO Shuttle Services';
    };
  }, [cmsContent]);

  if (cmsLoading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050509]">
      <Header activePage="Transferts" />
      
      <main>
        {/* HEADER COMPACT */}
        <section className="relative h-[450px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <SafeImage 
              src={heroImage}
              fallbackSrc={DEFAULT_HERO_IMAGE}
              alt="Transferts VTC"
              className="w-full h-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(90deg, rgba(11,11,11,0.9) 0%, rgba(11,11,11,0.7) 40%, rgba(11,11,11,0) 80%)'
              }}
            />
          </div>

          <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="max-w-3xl space-y-4">
              <p className="text-[10px] font-medium tracking-[0.35em] uppercase text-[#C9A24D]">
                Service de transferts privés
              </p>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#E5E5E5] leading-tight" style={{ textShadow: '0px 2px 10px rgba(0,0,0,0.5)' }}>
                Transferts privés <span className="text-[#C9A24D]">VTC</span>
              </h1>
              <p className="text-[#E5E5E5]/70 text-lg">
                Le Havre & toute la France
              </p>
              <p className="text-[#E5E5E5]/60 text-base max-w-2xl">
                Ponctualité garantie • Confort premium • Fiabilité à chaque trajet
              </p>
              <div className="pt-4">
                <Link to="/booking">
                  <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold px-8 py-3 rounded-none">
                    <Calendar className="w-4 h-4 mr-2" />
                    Réserver un transfert
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 1 : TRANSFERTS AU HAVRE - Expertise Locale */}
        <section className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Transferts au <span className="text-[#C9A24D]">Havre</span>
              </h2>
              <p className="text-[#A7A7A7] text-base max-w-3xl mx-auto">
                Une connaissance parfaite du territoire pour une prise en charge sans faille
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              {/* Points de départ locaux */}
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#C9A24D]/12 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#C9A24D]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#F5F5F5]">
                    Points de départ
                  </h3>
                </div>
                <div className="space-y-3">
                  {[
                    { point: 'Terminal croisières du Havre', detail: 'Accueil au pied du navire, gestion complète des bagages' },
                    { point: 'Gare du Havre', detail: 'Prise en charge sortie quais, suivi horaires SNCF' },
                    { point: 'Centre-ville & hôtels', detail: 'Tous quartiers, adresse précise' },
                    { point: 'Zones d\'affaires', detail: 'Port 2000, zones industrielles, parcs d\'activités' },
                    { point: 'Domiciles privés', detail: 'Toute agglomération havraise' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 bg-[#0B0B0B]/40 p-3">
                      <Check className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-[#F5F5F5] text-sm font-semibold">{item.point}</p>
                        <p className="text-[#A7A7A7] text-xs mt-0.5">{item.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Destinations locales */}
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#C9A24D]/12 flex items-center justify-center">
                    <Map className="w-6 h-6 text-[#C9A24D]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#F5F5F5]">
                    Destinations locales
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { ville: 'Honfleur', temps: '30 min', prix: '80€' },
                    { ville: 'Deauville', temps: '45 min', prix: '120€' },
                    { ville: 'Étretat', temps: '35 min', prix: '90€' },
                    { ville: 'Fécamp', temps: '40 min', prix: '100€' },
                    { ville: 'Rouen', temps: '1h', prix: '140€' },
                    { ville: 'Caen', temps: '1h15', prix: '160€' }
                  ].map((dest) => (
                    <div key={dest.ville} className="bg-[#0B0B0B]/40 p-3 text-center">
                      <p className="text-[#F5F5F5] font-semibold text-sm mb-1">{dest.ville}</p>
                      <p className="text-[#A7A7A7] text-xs mb-1">~{dest.temps}</p>
                      <p className="text-[#C9A24D] text-sm font-bold">Dès {dest.prix}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[#A7A7A7]/60 text-xs mt-4 italic">
                  * Tarifs berline 1-3 passagers. Autres véhicules sur demande.
                </p>
              </div>
            </div>

            {/* Maîtrise du terrain local */}
            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm border-l-4 border-[#C9A24D]">
              <div className="flex items-start gap-4">
                <Anchor className="w-6 h-6 text-[#C9A24D] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-[#F5F5F5] font-semibold mb-2">Expertise du port du Havre</h4>
                  <p className="text-[#D4D4D4] text-sm leading-relaxed">
                    Nos chauffeurs connaissent parfaitement les <strong>flux portuaires</strong>, les <strong>horaires d'accostage</strong> et les <strong>procédures de débarquement</strong>. Pour les croisiéristes : suivi en temps réel de votre navire, coordination avec les horaires d'embarquement, retour garanti à l'heure. Pour les professionnels du port : accès zones restreintes, connaissance des prestataires locaux.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2 : GARES & AÉROPORTS */}
        <section className="py-20 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Gares & <span className="text-[#C9A24D]">Aéroports</span>
              </h2>
              <p className="text-[#A7A7A7] text-base max-w-3xl mx-auto">
                Transferts vers tous les hubs de transport avec suivi en temps réel
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Aéroports */}
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#C9A24D]/12 flex items-center justify-center">
                    <Plane className="w-6 h-6 text-[#C9A24D]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#F5F5F5]">
                    Aéroports
                  </h3>
                </div>
                <div className="space-y-4 mb-6">
                  {[
                    { aeroport: 'Paris CDG', terminals: 'Tous terminaux (1, 2A-F, 3)', temps: '~2h15', prix: '280-320€' },
                    { aeroport: 'Paris Orly', terminals: 'Orly Sud & Ouest', temps: '~2h', prix: '260-300€' },
                    { aeroport: 'Beauvais', terminals: 'Terminal unique', temps: '~1h45', prix: '220-260€' },
                    { aeroport: 'Deauville', terminals: 'Aviation d\'affaires', temps: '~45 min', prix: '140-180€' }
                  ].map((aero) => (
                    <div key={aero.aeroport} className="bg-[#0B0B0B]/50 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[#F5F5F5] font-semibold">{aero.aeroport}</p>
                        <p className="text-[#C9A24D] font-bold text-sm">{aero.prix}</p>
                      </div>
                      <p className="text-[#A7A7A7] text-xs mb-1">{aero.terminals}</p>
                      <p className="text-[#A7A7A7] text-xs">Temps de trajet : {aero.temps}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-[#C9A24D]/20 pt-4">
                  <h4 className="text-[#C9A24D] text-xs uppercase tracking-wider mb-3">Services inclus</h4>
                  <div className="space-y-2">
                    {[
                      'Suivi de vol en temps réel',
                      'Ajustement automatique si retard',
                      'Attente gratuite 60 minutes',
                      'Accueil avec pancarte nominative',
                      'Aide bagages systématique'
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                        <span className="text-[#D4D4D4] text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Gares */}
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#C9A24D]/12 flex items-center justify-center">
                    <Train className="w-6 h-6 text-[#C9A24D]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#F5F5F5]">
                    Gares
                  </h3>
                </div>
                <div className="space-y-4 mb-6">
                  {[
                    { gare: 'Gare du Havre', detail: 'Sortie quais ou parvis', temps: '5-10 min', prix: '25-35€' },
                    { gare: 'Gare de Rouen', detail: 'Rive-Droite', temps: '~1h', prix: '140-160€' },
                    { gare: 'Paris Saint-Lazare', detail: 'Sortie Cour de Rome', temps: '~2h', prix: '260-300€' },
                    { gare: 'Paris Montparnasse', detail: 'Sortie principale', temps: '~2h15', prix: '270-310€' },
                    { gare: 'Paris Gare du Nord', detail: 'Eurostar & Thalys', temps: '~2h20', prix: '280-320€' }
                  ].map((gare) => (
                    <div key={gare.gare} className="bg-[#0B0B0B]/50 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[#F5F5F5] font-semibold">{gare.gare}</p>
                        <p className="text-[#C9A24D] font-bold text-sm">{gare.prix}</p>
                      </div>
                      <p className="text-[#A7A7A7] text-xs mb-1">{gare.detail}</p>
                      <p className="text-[#A7A7A7] text-xs">Temps : {gare.temps}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-[#C9A24D]/20 pt-4">
                  <h4 className="text-[#C9A24D] text-xs uppercase tracking-wider mb-3">Services inclus</h4>
                  <div className="space-y-2">
                    {[
                      'Suivi horaires SNCF en temps réel',
                      'Ajustement si retard de train',
                      'Attente gratuite 30 minutes',
                      'Accueil personnalisé',
                      'Aide au transport des bagages'
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                        <span className="text-[#D4D4D4] text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Garanties spécifiques */}
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { 
                  icon: Clock,
                  title: 'Ponctualité',
                  desc: 'Monitoring en temps réel de votre vol ou train. Chauffeur toujours à l\'heure, même en cas d\'imprévu.'
                },
                { 
                  icon: Phone,
                  title: 'Joignabilité',
                  desc: 'Contact direct avec votre chauffeur 30 min avant. Assistance 24/7 en cas de besoin.'
                },
                { 
                  icon: Shield,
                  title: 'Fiabilité',
                  desc: 'Véhicules récents entretenus. Chauffeurs agréés VTC. Assurance tous risques.'
                }
              ].map((item) => (
                <div key={item.title} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 text-center backdrop-blur-sm">
                  <item.icon className="w-8 h-8 text-[#C9A24D] mx-auto mb-3" />
                  <h4 className="text-[#F5F5F5] font-semibold mb-2">{item.title}</h4>
                  <p className="text-[#A7A7A7] text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 3 : LONGS TRAJETS - Couverture Nationale */}
        <section className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Longs trajets <span className="text-[#C9A24D]">partout en France</span>
              </h2>
              <p className="text-[#A7A7A7] text-base max-w-3xl mx-auto">
                Y GO opère au-delà du Havre grâce à un réseau de partenaires sélectionnés dans toute la France
              </p>
            </div>

            {/* Capacité nationale */}
            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm mb-8">
              <div className="grid lg:grid-cols-2 gap-8">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Globe className="w-8 h-8 text-[#C9A24D]" />
                    <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F5]">
                      Couverture nationale
                    </h3>
                  </div>
                  <p className="text-[#D4D4D4] text-sm leading-relaxed mb-6">
                    Pour les trajets au-delà de 200 km ou en dehors de la Normandie, <strong className="text-[#F5F5F5]">Y GO coordonne votre transfert avec des partenaires certifiés</strong> présents dans toute la France. Vous gardez un <strong>interlocuteur unique</strong> (Y GO) et bénéficiez des <strong>mêmes standards de qualité</strong>.
                  </p>
                  <div className="space-y-2">
                    {[
                      'Un seul contact : Y GO',
                      'Standards de service uniformes',
                      'Partenaires certifiés VTC',
                      'Facturation centralisée',
                      'Suivi de bout en bout'
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                        <span className="text-[#D4D4D4] text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-[#C9A24D] text-sm uppercase tracking-wider mb-4">Exemples de longs trajets</h4>
                  <div className="space-y-3">
                    {[
                      { de: 'Le Havre', vers: 'Lyon', km: '~700 km', prix: 'Sur devis' },
                      { de: 'Le Havre', vers: 'Bordeaux', km: '~650 km', prix: 'Sur devis' },
                      { de: 'Le Havre', vers: 'Lille', km: '~300 km', prix: 'Sur devis' },
                      { de: 'Le Havre', vers: 'Strasbourg', km: '~650 km', prix: 'Sur devis' },
                      { de: 'Le Havre', vers: 'Marseille', km: '~950 km', prix: 'Sur devis' }
                    ].map((trajet, i) => (
                      <div key={i} className="flex items-center justify-between bg-[#0B0B0B]/50 p-3 border-l-2 border-[#C9A24D]/30">
                        <div className="flex items-center gap-3">
                          <MapPin className="w-4 h-4 text-[#C9A24D] flex-shrink-0" />
                          <span className="text-[#F5F5F5] text-sm">{trajet.de} → {trajet.vers}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-[#A7A7A7] text-xs">{trajet.km}</span>
                          <span className="text-[#C9A24D] text-sm font-semibold">{trajet.prix}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[#A7A7A7] text-xs mt-4 italic">
                    Tarifs variables selon distance, véhicule et options. Devis gratuit sous 2h.
                  </p>
                </div>
              </div>
            </div>

            {/* Types de trajets longs */}
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              {[
                {
                  title: 'Déplacements professionnels',
                  desc: 'Rendez-vous clients, salons, congrès dans toute la France',
                  icon: Building2
                },
                {
                  title: 'Trajets privés longue distance',
                  desc: 'Voyages familiaux, déménagements, événements personnels',
                  icon: Users
                },
                {
                  title: 'Transferts inter-villes',
                  desc: 'Liaisons régulières entre deux villes pour professionnels',
                  icon: Map
                }
              ].map((type) => (
                <div key={type.title} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm">
                  <type.icon className="w-8 h-8 text-[#C9A24D] mb-3" />
                  <h4 className="text-[#F5F5F5] font-semibold mb-2 text-sm">{type.title}</h4>
                  <p className="text-[#A7A7A7] text-xs leading-relaxed">{type.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 4 : RÉSEAU DE PARTENAIRES */}
        <section className="py-20 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Notre réseau de <span className="text-[#C9A24D]">partenaires</span>
              </h2>
              <p className="text-[#A7A7A7] text-base max-w-3xl mx-auto">
                Des standards de qualité uniformes sur l'ensemble du territoire
              </p>
            </div>

            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm mb-8">
              <div className="flex items-start gap-4 mb-6">
                <Network className="w-10 h-10 text-[#C9A24D] flex-shrink-0" />
                <div>
                  <h3 className="text-[#F5F5F5] text-lg font-semibold mb-2">
                    Comment fonctionne notre réseau ?
                  </h3>
                  <p className="text-[#D4D4D4] text-sm leading-relaxed">
                    Pour les trajets en dehors de notre zone d'intervention directe, Y GO <strong>sélectionne et coordonne</strong> des chauffeurs partenaires répondant à nos critères stricts. Vous réservez via Y GO, vous payez Y GO, vous avez un seul interlocuteur. <strong className="text-[#C9A24D]">La qualité de service reste identique.</strong>
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-[#C9A24D] text-sm uppercase tracking-wider mb-4">Critères de sélection partenaires</h4>
                  <div className="space-y-2">
                    {[
                      'Licence VTC en règle',
                      'Véhicules premium récents (<3 ans)',
                      'Note client minimale 4.7/5',
                      'Assurance professionnelle vérifiée',
                      'Formation service client',
                      'Maîtrise de l\'anglais'
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                        <span className="text-[#D4D4D4] text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-[#C9A24D] text-sm uppercase tracking-wider mb-4">Votre garantie</h4>
                  <div className="space-y-2">
                    {[
                      'Un seul interlocuteur : Y GO',
                      'Même niveau de service partout',
                      'Facturation centralisée',
                      'Assistance Y GO 24/7',
                      'Réclamation gérée par Y GO',
                      'Engagement qualité identique'
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Shield className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                        <span className="text-[#D4D4D4] text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Zone de couverture */}
            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm border-l-4 border-[#C9A24D]">
              <div className="flex items-start gap-4">
                <Map className="w-6 h-6 text-[#C9A24D] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-[#F5F5F5] font-semibold mb-2">Zone de couverture</h4>
                  <p className="text-[#D4D4D4] text-sm leading-relaxed">
                    <strong>Services directs Y GO</strong> : Le Havre et Normandie (Rouen, Caen, Honfleur, Deauville, Étretat). 
                    <strong className="text-[#C9A24D]"> Services via réseau partenaire</strong> : Île-de-France, Grand Est, Hauts-de-France, Pays de la Loire, et toute la France sur demande.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5 : POURQUOI CHOISIR Y GO */}
        <section className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Pourquoi choisir <span className="text-[#C9A24D]">Y GO</span> pour vos transferts ?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  title: 'Disponibilité totale',
                  icon: Clock,
                  points: [
                    'Service 24h/24, 7j/7, 365 jours par an',
                    'Réservation possible jusqu\'à 2h avant le départ',
                    'Capacité d\'intervention immédiate sur Le Havre',
                    'Planning flexible pour trajets récurrents'
                  ]
                },
                {
                  title: 'Flexibilité & personnalisation',
                  icon: Users,
                  points: [
                    'Modification gratuite jusqu\'à 12h avant',
                    'Arrêts intermédiaires possibles',
                    'Choix du véhicule selon vos besoins',
                    'Options à la carte (siège enfant, bagages XXL, etc.)'
                  ]
                },
                {
                  title: 'Expérience client premium',
                  icon: Star,
                  points: [
                    'Véhicules premium (Mercedes, Tesla, VW)',
                    'Chauffeurs en tenue professionnelle',
                    'WiFi gratuit + eau minérale à bord',
                    'Paiement CB sans frais à bord'
                  ]
                },
                {
                  title: 'Fiabilité opérationnelle',
                  icon: Shield,
                  points: [
                    'Taux de ponctualité > 98%',
                    'Véhicules de secours en cas d\'imprévu',
                    'Double vérification avant chaque course',
                    'Assistance technique 24/7'
                  ]
                }
              ].map((reason) => (
                <div key={reason.title} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#C9A24D]/12 flex items-center justify-center">
                      <reason.icon className="w-5 h-5 text-[#C9A24D]" />
                    </div>
                    <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F5]">
                      {reason.title}
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {reason.points.map((point, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                        <span className="text-[#D4D4D4] text-sm leading-relaxed">{point}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CAS D'USAGE TYPIQUES */}
        <section className="py-20 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Cas d'usage <span className="text-[#C9A24D]">typiques</span>
              </h2>
              <p className="text-[#A7A7A7] text-base">
                Des scénarios réels pour mieux comprendre notre service
              </p>
            </div>

            <div className="space-y-6">
              {[
                {
                  scenario: 'Croisiériste international',
                  desc: 'Navire accoste au Havre 8h, vol retour CDG 18h',
                  solution: 'Transfert direct port → CDG (2h15). Départ 14h30, arrivée 16h45. Temps libre pour check-in tranquille.',
                  prix: '~300€'
                },
                {
                  scenario: 'Professionnel en déplacement',
                  desc: 'Rendez-vous client Paris 10h, retour Le Havre en fin de journée',
                  solution: 'Départ Le Havre 7h30, arrivée Paris 9h30. Attente ou mise à disposition. Retour 18h, arrivée 20h.',
                  prix: '~600€ A/R'
                },
                {
                  scenario: 'Famille avec enfants',
                  desc: 'Vol tôt matin CDG (6h), domicile Le Havre',
                  solution: 'Départ domicile 3h15, arrivée CDG 5h30. Sièges enfants installés. Aide bagages famille nombreuse.',
                  prix: '~300€'
                },
                {
                  scenario: 'Groupe d\'affaires',
                  desc: '6 personnes, séminaire Deauville, arrivée Orly',
                  solution: 'Van 7 places, accueil Orly avec pancarte, transfert direct Deauville (2h). Bagages + matériel séminaire.',
                  prix: '~400€'
                }
              ].map((cas, i) => (
                <div key={i} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#C9A24D] flex items-center justify-center flex-shrink-0 text-[#050509] font-bold text-sm">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-[#F5F5F5] font-semibold mb-1">{cas.scenario}</h4>
                      <p className="text-[#A7A7A7] text-sm mb-3">{cas.desc}</p>
                      <p className="text-[#D4D4D4] text-sm leading-relaxed mb-2">
                        <strong className="text-[#C9A24D]">Solution Y GO :</strong> {cas.solution}
                      </p>
                      <p className="text-[#C9A24D] text-sm font-semibold">{cas.prix}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA FINAL SOBRE */}
        <section className="py-20 bg-gradient-to-r from-[#111111] to-[#1F1F1F] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
              Besoin d'un <span className="text-[#C9A24D]">transfert</span> ?
            </h2>
            <p className="text-[#A7A7A7] text-base mb-8 max-w-2xl mx-auto">
              Réservez en ligne pour un trajet standard, ou contactez-nous pour un devis personnalisé
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
              <Link to="/booking">
                <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold px-10 py-4 rounded-none text-base">
                  <Calendar className="w-5 h-5 mr-2" />
                  Réserver en ligne
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10 px-10 py-4 rounded-none text-base"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Demander un devis sur mesure
                </Button>
              </Link>
            </div>

            {/* Contact rapide */}
            <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <a 
                href="tel:+33278780404"
                className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-6 text-center backdrop-blur-sm group"
              >
                <Phone className="w-6 h-6 text-[#C9A24D] mx-auto mb-3" />
                <p className="text-[#E5E5E5] font-medium group-hover:text-[#C9A24D] transition-colors">
                  +33 2 78 78 04 04
                </p>
                <p className="text-[#A7A7A7] text-xs mt-1">Assistance 24/7</p>
              </a>
              <a 
                href="mailto:booking@y-go.fr"
                className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-6 text-center backdrop-blur-sm group"
              >
                <Mail className="w-6 h-6 text-[#C9A24D] mx-auto mb-3" />
                <p className="text-[#E5E5E5] font-medium group-hover:text-[#C9A24D] transition-colors">
                  booking@y-go.fr
                </p>
                <p className="text-[#A7A7A7] text-xs mt-1">Réponse sous 2h</p>
              </a>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
